import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import ProfessionalResourcesSection from '../components/ProfessionalResourcesSection';
import { toast } from 'sonner';
import {
  GraduationCap, Plane, MapPin, FileText, Shield, CheckCircle,
  ArrowRight, Phone, Building2, Key, AlertTriangle, ExternalLink,
  ChevronRight, Globe, Home, Lock, Users, IndianRupee, DollarSign
} from 'lucide-react';

const FLOWS = {
  domestic: {
    id: 'domestic',
    label: 'Moving Within India',
    icon: <MapPin className="h-5 w-5" />,
    tagline: 'Relocating to a new city for work, studies, or family',
    color: '#E07A3A',
    steps: [
      {
        icon: <Building2 className="h-6 w-6" />,
        title: 'Browse Verified Rentals',
        desc: 'Search rooms, PGs, and flats on Shopperden with photos, maps, and verified landlord profiles. Filter by city, budget, and furnishing.',
        tip: 'Start browsing 2-3 weeks before your move date. Popular areas fill up fast.',
      },
      {
        icon: <Phone className="h-6 w-6" />,
        title: 'Connect with Landlord',
        desc: 'Once you find a room you like, message the landlord or letting agent directly through Shopperden. Ask about deposit, maintenance, and house rules, and agree viewings in-app.',
        tip: 'Always ask for a video tour if you cannot visit in person. Verify the landlord\'s Aadhaar/ID on the platform.',
      },
      {
        icon: <FileText className="h-6 w-6" />,
        title: 'Sign Rent Agreement',
        desc: 'Download the Model Tenancy Agreement from the government portal. Both parties sign — this protects your deposit and rights as a tenant.',
        tip: 'Get the agreement registered at the local Sub-Registrar office. An e-stamp of ₹100-500 is usually sufficient.',
        link: { url: 'https://mohua.gov.in/cms/Model-Tenancy-Act.php', label: 'Model Tenancy Act — Download Template' },
      },
      {
        icon: <Key className="h-6 w-6" />,
        title: 'Move In Securely',
        desc: 'Pay deposit via UPI/bank transfer (get a receipt), collect keys, and do a walk-through checklist for existing damage. You\'re home!',
        tip: 'Take timestamped photos of every room on move-in day. This prevents disputes about damage when you leave.',
      },
    ],
    resources: [
      { title: 'Model Tenancy Act 2021', url: 'https://mohua.gov.in/cms/Model-Tenancy-Act.php', desc: 'Official rent agreement template & tenant rights' },
      { title: 'Tenant Rights by State', url: 'https://www.naredco.in', desc: 'State-wise rental laws and dispute resolution' },
      { title: 'Police Verification', url: 'https://passportindia.gov.in', desc: 'Tenant police verification (required in most cities)' },
    ],
    feeNote: 'Tenant access on Shopperden India: ₹499 one-time platform fee',
    feeCurrency: '₹',
    feeAmount: 499,
  },
  abroad: {
    id: 'abroad',
    label: 'Going to Canada',
    icon: <Plane className="h-5 w-5" />,
    tagline: 'Students & professionals securing accommodation before landing',
    color: '#3B82F6',
    steps: [
      {
        icon: <Globe className="h-6 w-6" />,
        title: 'Search Canadian Rentals from India',
        desc: 'Browse verified rooms, shared apartments, and basement suites across Toronto, Vancouver, Calgary, and more — all from India, before you fly.',
        tip: 'Start searching 4-6 weeks before your arrival date. Landlords prefer tenants who are ready to sign early.',
      },
      {
        icon: <Users className="h-6 w-6" />,
        title: 'Verify & Connect Remotely',
        desc: 'Shopperden verifies landlords so you can trust listings. Request a video walkthrough, ask about utilities, transit access, and lease terms.',
        tip: 'Ask landlords if they accept international students. Many offer flexible move-in dates for visa holders.',
      },
      {
        icon: <FileText className="h-6 w-6" />,
        title: 'Sign Lease Agreement Online',
        desc: 'Use the standard Ontario/BC residential tenancy agreement. E-sign before you arrive. Pay first & last month deposit via bank transfer.',
        tip: 'Never send money without a signed lease. Use Shopperden\'s secure platform for all communications.',
        link: { url: 'https://www.ontario.ca/page/renting-ontario-your-rights', label: 'Ontario Tenancy Agreement — Official Form' },
      },
      {
        icon: <Key className="h-6 w-6" />,
        title: 'Arrive & Collect Keys',
        desc: 'Your room is ready when you land. Do a condition inspection report with the landlord, set up utilities, and settle in with peace of mind.',
        tip: 'Keep all receipts. In Canada, landlords must return your deposit within 15-30 days of lease end (varies by province).',
      },
    ],
    resources: [
      { title: 'Ontario Tenancy Rights', url: 'https://www.ontario.ca/page/renting-ontario-your-rights', desc: 'Official lease form & tenant rights in Ontario' },
      { title: 'BC Residential Tenancy', url: 'https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies', desc: 'British Columbia tenancy branch & forms' },
      { title: 'Canada Mortgage & Housing', url: 'https://www.cmhc-schl.gc.ca/consumers/renting', desc: 'CMHC renting guide for newcomers' },
      { title: 'IRCC Student Housing', url: 'https://www.canada.ca/en/immigration-refugees-citizenship.html', desc: 'Immigration resources for international students' },
    ],
    feeNote: 'Tenant access on Shopperden Canada: C$15 one-time platform fee',
    feeCurrency: 'C$',
    feeAmount: 15,
  },
};

const StepCard = ({ step, index, color }) => {
  const [showTip, setShowTip] = useState(false);
  return (
    <div className="relative" data-testid={`tenant-step-${index}`}>
      {/* Connector line */}
      {index > 0 && (
        <div className="absolute -top-6 left-8 w-px h-6" style={{ backgroundColor: `${color}30` }} />
      )}
      <div className="border border-slate-100 hover:border-slate-200 transition-all group">
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center" style={{ backgroundColor: `${color}12`, color }}>
              {step.icon}
              <span className="absolute -top-1 -left-1 w-5 h-5 text-[10px] font-bold text-white flex items-center justify-center" style={{ backgroundColor: color }}>
                {index + 1}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-['Playfair_Display'] text-lg sm:text-xl text-[#0F172A] font-semibold mb-1.5">{step.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{step.desc}</p>
              {step.link && (
                <a href={step.link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold hover:underline" style={{ color }} data-testid={`tenant-link-${index}`}>
                  <ExternalLink className="h-3.5 w-3.5" />
                  {step.link.label}
                </a>
              )}
            </div>
          </div>
          {/* Tip toggle */}
          <button
            onClick={() => setShowTip(!showTip)}
            className="mt-3 flex items-center gap-1.5 text-xs tracking-wider uppercase font-semibold transition-colors"
            style={{ color: showTip ? color : '#94a3b8' }}
            data-testid={`tenant-tip-toggle-${index}`}
          >
            <Shield className="h-3.5 w-3.5" />
            {showTip ? 'Hide Tip' : 'Shopperden Tip'}
          </button>
          {showTip && (
            <div className="mt-3 p-3 border text-sm leading-relaxed" style={{ backgroundColor: `${color}08`, borderColor: `${color}20`, color: '#334155' }} data-testid={`tenant-tip-content-${index}`}>
              <span className="font-semibold text-xs uppercase tracking-wider block mb-1" style={{ color }}>Pro Tip</span>
              {step.tip}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TenantRentingGuidePage = () => {
  const { user } = useAuth();
  const { country } = useCountry();
  const [activeFlow, setActiveFlow] = useState('domestic');
  const flow = FLOWS[activeFlow];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="tenant-renting-guide-page">
      <Header />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#E07A3A]/10 text-[#E07A3A] text-xs tracking-[0.25em] uppercase font-bold mb-4">
            <GraduationCap className="h-4 w-4" />
            For Students &amp; Newcomers
          </div>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl text-[#0F172A] font-semibold leading-tight">
            Secure Your Room<span className="italic text-[#E07A3A]"> Before You Move</span>
          </h1>
          <p className="text-slate-500 mt-4 max-w-2xl mx-auto text-sm leading-relaxed">
            Whether you're a student heading to Canada or relocating within India — follow this step-by-step guide to find verified rooms, sign a tenancy agreement, and move in with confidence.
          </p>
        </div>

        {/* Flow Toggle */}
        <div className="flex justify-center gap-3 mb-12" data-testid="tenant-flow-toggle">
          {Object.values(FLOWS).map((f) => (
            <button
              key={f.id}
              onClick={() => setActiveFlow(f.id)}
              className={`flex items-center gap-2.5 px-6 py-3.5 text-sm font-semibold tracking-wider uppercase transition-all border ${
                activeFlow === f.id
                  ? 'text-white shadow-lg'
                  : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700'
              }`}
              style={activeFlow === f.id ? { backgroundColor: f.color, borderColor: f.color } : {}}
              data-testid={`tenant-flow-${f.id}`}
            >
              {f.icon}
              <div className="text-left">
                <span className="block">{f.label}</span>
                <span className={`block text-[10px] tracking-normal normal-case font-normal mt-0.5 ${activeFlow === f.id ? 'text-white/70' : 'text-slate-400'}`}>{f.tagline}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Steps */}
        <div className="max-w-3xl mx-auto space-y-6 mb-12" data-testid={`tenant-steps-${activeFlow}`}>
          {flow.steps.map((step, i) => (
            <div key={`${activeFlow}-${i}`} className="relative">
              {i > 0 && !user && (
                <div className="absolute inset-0 z-10 bg-white/80 backdrop-blur-[2px] flex items-center justify-center" data-testid={`tenant-lock-${i}`}>
                  <div className="text-center p-4">
                    <Lock className="h-6 w-6 text-red-500 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-red-600">Please login to access this step</p>
                    <Link to="/register">
                      <Button className="mt-2 bg-red-500 text-white hover:bg-red-600 text-xs tracking-widest uppercase font-semibold px-4 py-2" data-testid={`tenant-lock-register-${i}`}>
                        Sign Up Free
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
              <StepCard step={step} index={i} color={flow.color} />
            </div>
          ))}
        </div>

        {/* Fee Note */}
        <div className="max-w-3xl mx-auto mb-12">
          <div className="flex items-center justify-between p-5 border border-slate-100 bg-white" data-testid="tenant-fee-note">
            <div className="flex items-center gap-3">
              {activeFlow === 'domestic' ? <IndianRupee className="h-5 w-5 text-[#E07A3A]" /> : <DollarSign className="h-5 w-5 text-blue-500" />}
              <div>
                <p className="text-sm font-semibold text-[#0F172A]">{flow.feeNote}</p>
                <p className="text-xs text-slate-400 mt-0.5">No brokerage. No hidden charges. One-time only.</p>
              </div>
            </div>
            <Link to="/register">
              <Button className="text-white text-xs tracking-widest uppercase font-semibold px-6 py-4" style={{ backgroundColor: flow.color }} data-testid="tenant-register-btn">
                Get Started <ArrowRight className="ml-2 h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Safety Banner */}
        <div className="max-w-3xl mx-auto mb-12">
          <div className="p-5 bg-amber-50 border border-amber-100" data-testid="tenant-safety-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-amber-800 text-sm mb-1">Stay Safe — Avoid Rental Scams</h4>
                <ul className="text-xs text-amber-700 space-y-1 leading-relaxed">
                  <li>Never pay deposit without a signed tenancy agreement</li>
                  <li>Always verify the landlord's identity through Shopperden's verification system</li>
                  <li>Request a video tour if you cannot visit the property in person</li>
                  <li>Keep all payment receipts and communication records</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Government Resources */}
        <div className="max-w-3xl mx-auto mb-12" data-testid="tenant-resources">
          <h3 className="font-['Playfair_Display'] text-xl text-[#0F172A] font-semibold mb-4 text-center">
            Official Government Resources
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {flow.resources.map((res) => (
              <a
                key={res.title}
                href={res.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 p-4 border border-slate-100 bg-white hover:border-slate-300 transition-colors group"
                data-testid={`tenant-resource-${res.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <ExternalLink className="h-4 w-4 flex-shrink-0 mt-0.5 group-hover:text-[#E07A3A] transition-colors" style={{ color: flow.color }} />
                <div>
                  <p className="text-sm font-semibold text-[#0F172A] group-hover:text-[#E07A3A] transition-colors">{res.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{res.desc}</p>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Professional directory deep-links */}
        <div className="mb-6">
          <ProfessionalResourcesSection context="renting" country={country || 'UK'} />
        </div>

        {/* CTA */}
        <div className="bg-[#0F172A] p-8 sm:p-10 text-center" data-testid="tenant-cta-section">
          <GraduationCap className="h-10 w-10 text-[#E07A3A] mx-auto mb-4" />
          <h3 className="font-['Playfair_Display'] text-2xl text-white font-semibold mb-2">Ready to Find Your Room?</h3>
          <p className="text-white/50 text-sm max-w-lg mx-auto mb-6">
            Browse verified rooms across India and Canada — or match with a letting agent who can handle the whole tenancy for you.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/properties">
              <Button className="bg-[#E07A3A] text-white hover:bg-[#C4652E] px-8 py-5 text-sm tracking-widest uppercase font-semibold" data-testid="tenant-browse-btn">
                Browse Rentals <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/buying-guide">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 px-8 py-5 text-sm tracking-widest uppercase font-semibold" data-testid="tenant-buying-guide-btn">
                Buying Guide
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantRentingGuidePage;
